import LoadMoreButton from '@rio-cloud/rio-uikit/components/loadMore/LoadMoreButton';
import useSearchHighlight from '@rio-cloud/rio-uikit/hooks/useSearchHighlight';
import Table, { TableBody, TableHeader } from '@rio-cloud/rio-uikit/Table';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { NoDataView } from '#components/shared/NoDataView.tsx';
import { TRACKING_CATEGORIES } from '#configuration/setup/googleAnalytics.ts';
import type { Driver } from '#domain/model/Driver.ts';
import type { Tour } from '#domain/model/Tour.ts';
import { TourTableHeader } from '#features/tours/table/TourTableHeader.tsx';
import { TourTableRow } from '#features/tours/table/TourTableRow.tsx';
import { TourTableToolbar } from '#features/tours/table/TourTableToolbar.tsx';
import { addSortableTourKey, getTourDriver, type TourWithSortableKey } from '#features/tours/table/tours.util.ts';
import { useDebouncedSearch } from '#hooks/shared/useDebouncedSearch.ts';
import { parseAsArrayOf, stringParser, useSearchParam } from '#hooks/shared/useSearchParam.ts';
import { useTableSorting } from '#hooks/shared/useTableSorting.ts';

import { useFilteredTours } from '../hooks/useFilteredTours.ts';

type TourTableProps = {
    tours: Tour[];
    drivers: Driver[];
    isFetchingTours: boolean;
    handleTourRefetch: () => void;
    selectTour: (tourId: string | undefined) => void;
    selectedTourId: string | undefined;
};

export const TourTable = ({
    tours,
    drivers,
    isFetchingTours,
    handleTourRefetch,
    selectTour,
    selectedTourId,
}: TourTableProps) => {
    const intl = useIntl();

    const tableRef = useRef<HTMLDivElement | null>(null);
    const { search, searchInput, onSearch } = useDebouncedSearch();
    const [statusFilter, setStatusFilter, resetStatusFilter] = useSearchParam(
        'status',
        parseAsArrayOf(stringParser).default([])
    );

    const dataWithSortableKey = addSortableTourKey(tours, drivers);

    const {
        sortedItems: sortedTours,
        sortKey,
        sortDirection,
        handleSortByColumn,
    } = useTableSorting<TourWithSortableKey>(dataWithSortableKey, 'statusWeight', 'lastStopEndTarget');

    const { filteredTours, activeTour } = useFilteredTours({
        tours: sortedTours,
        drivers,
        search,
        statusFilter,
        selectedTourId,
    });

    useSearchHighlight({
        ref: tableRef,
        query: searchInput,
        caseSensitive: false,
        deps: [filteredTours],
    });

    const onFilterStatus = (input: string[]) => setStatusFilter(input);

    const isTableEmpty = filteredTours.length === 0;

    return (
        <>
            <TourTableToolbar
                search={searchInput}
                onSearch={onSearch}
                statusFilter={statusFilter}
                onFilterStatus={onFilterStatus}
                onRefresh={handleTourRefetch}
                isRefreshing={isFetchingTours}
                onResetStatusFilter={resetStatusFilter}
            />
            <div ref={tableRef}>
                <Table
                    rowKey='id'
                    hover={!isTableEmpty}
                    gridLines
                    rounded={false}
                    activeRowId={activeTour?.id}
                    sortBy={sortKey as string}
                    sortDirection={sortDirection}
                    onSortChange={columnKey => handleSortByColumn(columnKey as keyof TourWithSortableKey)}
                    onRowClick={tour => selectTour(tour.id as string)}
                    noRowsState={
                        <NoDataView
                            title={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.common.no-data-view.title',
                            })}
                            message={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.common.no-data-view.label',
                            })}
                            trackingCategory={TRACKING_CATEGORIES.TOURS}
                            trackingLabel={'Show no data view in tours table'}
                        />
                    }
                >
                    <TableHeader sticky>
                        <TourTableHeader />
                    </TableHeader>
                    <TableBody>
                        {filteredTours.map(tour => (
                            <TourTableRow key={tour.id} tour={tour} driver={getTourDriver(tour, drivers)} />
                        ))}
                    </TableBody>
                </Table>
            </div>
            <LoadMoreButton loaded={filteredTours.length} total={tours.length} />
        </>
    );
};
